import React from 'react';

import { LegOptionContextProps } from 'Modules/MultiStopWizard/Components/Leg/Contexts/LegOptionContext';
import { isTripClosedByBookingType } from 'Services/Api/_HumanWritten/BookingWizardDataService';

/**
 * The modifiers a sailing's row carries, from the state the wizard has already worked out.
 *
 * <p>Shared by both widths so a sailing that reads as sold out on a desktop cannot read as merely
 * quiet on a phone. The availability modifier is what colours the rail and the dot; `--quiet` is
 * what greys the whole row, and it is deliberately NOT the same question — a departed sailing and a
 * full one are both unbookable and are drawn alike, while a sailing with two seats left is coloured
 * but not greyed.</p>
 */
export function tripOptionStateClasses(context: LegOptionContextProps): Record<string, boolean> {
	const { isSelectable, isVisuallyDisabled, availabilityStatus } = context;

	const unbookable =
		availabilityStatus === 'no-availability' ||
		availabilityStatus === 'departed' ||
		availabilityStatus === 'closed';

	return {
		'trip-option--disabled': !isSelectable,
		'trip-option--quiet': unbookable || (!!isVisuallyDisabled && !context.isSelected),
		'trip-option--high': availabilityStatus === 'high-availability',
		'trip-option--low': availabilityStatus === 'low-availability',
		'trip-option--none': availabilityStatus === 'no-availability',
		'trip-option--departed': availabilityStatus === 'departed',
		'trip-option--closed': availabilityStatus === 'closed',
		// Sold out to everyone else, still open to this one. The row says so in the left column and
		// greys its capacities, rather than drawing a row of red dots beside a price.
		'trip-option--sold-out-open': isSoldOutButBookable(context),
	};
}

/**
 * Pressing the chosen card puts the sailing back in the list.
 *
 * <p>The same act as the `Change …` control under it, and the same handler — a card that can only
 * be undone through one small link reads as a decision already made, and the row it replaced was
 * pressable right up until it was chosen.</p>
 *
 * <p>On the CARD, never on the shell around it: the control below shares that shell, and a click on
 * it would run this handler as well, de-selecting the sailing and re-selecting it in one press. The
 * notes disclosure sits inside the card and stops its own clicks for the same reason.</p>
 */
export function deselectProps(onClick?: () => void) {
	return {
		onClick,
		onKeyDown: (event: React.KeyboardEvent) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onClick?.();
			}
		},
		role: 'button' as const,
		tabIndex: 0,
	};
}

/**
 * Whether this sailing has already gone.
 *
 * <p>The trip's own flag first, because that is what `useLegErrorMessage` asks before it prints
 * `DEPARTED` — the availability status is a second opinion that mostly agrees and is kept as a
 * fallback for a row drawn before the flag is known.</p>
 */
export function hasDeparted(context: LegOptionContextProps): boolean {
	return context.trip?.departed === true || context.availabilityStatus === 'departed';
}

/**
 * Whether the row is the one that prints `SOLD OUT`.
 *
 * <p>In the order `useLegErrorMessage` decides it — gone, then closed, then full — so this is true
 * exactly when that word is on screen. It used to read `availabilityStatus === 'no-availability'`,
 * which is a different computation and does not always agree: a sold-out sailing can carry
 * `closed` there, and the row then kept its fare and put the waitlist button back inline.</p>
 */
export function isSoldOut(context: LegOptionContextProps): boolean {
	if (hasDeparted(context) || isTripClosedByBookingType(context.trip, context.wizardData.tabSelected)) {
		return false;
	}
	return context.soldOut === true;
}

/**
 * Sold out, yet nothing on the row says so.
 *
 * <p>That is the manager's view and only the manager's: `useLegErrorMessage` puts `SOLD OUT` behind
 * `if (!store.isManager)`, and `useIsLegSelectable` returns true for them — so a manager gets a
 * greyed, clickable row with no status on it, because they are allowed to overbook. Asked as
 * "sold out and unexplained" rather than by reading the role, so it keeps describing whoever the
 * two hooks above decide can do this.</p>
 */
export function isSoldOutButBookable(context: LegOptionContextProps): boolean {
	return isSoldOut(context) && !context.errorMessage;
}

/**
 * How far past full this sailing is, for the booking being made.
 *
 * <p>Zero where it is merely full and negative where it is already over — which are two different
 * situations to the one reader who can act on either. Read from the capacity that governs the tab,
 * because a vehicle booking is not stopped by a full passenger deck and the other way round.</p>
 */
export function soldOutMargin(context: LegOptionContextProps): number {
	if (context.wizardData?.tabSelected === 'vehicle') {
		return context.cargoSpaces ?? 0;
	}
	return context.paxSpaces ?? 0;
}

